import '@/stores/sessionState.ts' // register popstate listener before vue-router's
import { installDebugLog } from '@/utils/debugLog.ts'

// Mirror console into an in-app buffer (viewable on /test) as early as possible —
// release builds don't forward the WebView console to logcat.
installDebugLog()

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import './assets/main.css'
import 'barcode-detector/polyfill'

import App from './App.vue'
import router, { RouteName, START_PAGE_ROUTE_NAME } from './router'
import { Dialog } from '@capacitor/dialog'
import { useLocalizationStore } from '@/stores/localization.ts'
import { useThemeStore } from '@/stores/theme.ts'
import { useAuthStore, restoreAuthState, initSuperTokens } from '@/stores/auth.ts'
import { patchGlobalFetch } from '@/utils/fetch-client.ts'
import { isOffline } from '@/utils/networkStatus.ts'
import { useInfoStore } from '@/stores/info.ts'
import { shouldNavigateToStartPage } from '@/stores/startupNavigation.ts'
import { takeStartupDeepLinkPath } from '@/utils/startupDeepLink.ts'
import type { RouteLocationRaw, RouteLocationResolved } from 'vue-router'
import { usePreferencesStore } from '@/stores/preferences.ts'
import * as Sentry from '@sentry/capacitor'
import * as SentryVue from '@sentry/vue'
import { useUserCollectionStore } from '@/stores/user-collection'
import { useListsStore } from '@/stores/lists.ts'
import { useUserListsStore } from '@/stores/user-lists'
import { useUserSettingsStore } from '@/stores/user-settings'
import { useCatalogSyncStore } from '@/stores/catalog-sync'
import { usePaymentStore } from '@/stores/payment.ts'
import { enablePushNotificationsOnFirstStart } from '@/utils/pushNotifications.ts'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { version } from '../../package.json'
import './polyfills'
import { Capacitor } from '@capacitor/core'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

// Tell Capgo this bundle booted as early as possible. Capgo starts its rollback
// countdown (appReadyTimeout, ~10s) the instant set() reloads the webview. The
// signal only needs the bundle's JS to be executing — gating it behind
// router.isReady()/init() (which waits on the initial route's dynamic import)
// made set()-applied updates miss that window and get rolled back, so updates
// only ever stuck after a full restart. A bundle that fails to execute at all
// never reaches this line, so module-load crashes are still caught by rollback.
if (Capacitor.isNativePlatform()) {
  CapacitorUpdater.notifyAppReady().catch((e) => console.warn('notifyAppReady failed', e))
}

const app = createApp(App)

app.use(createPinia())
app.use(router)

Sentry.init({
  enabled: !import.meta.env.DEV,
  dsn: 'https://824691d387e2e34a09ec000c6bc4fdc9@o4508879400534016.ingest.de.sentry.io/4509043700858960',
  integrations: [
    SentryVue.browserTracingIntegration({ router }),
    SentryVue.httpClientIntegration(),
  ],
  release: 'atsume-app@' + version,
  sendDefaultPii: false,
  beforeSend(event) {
    // Drop events captured while the device has no connectivity (e.g. failed
    // requests auto-captured by the HTTP client integration). These are
    // expected offline conditions, not bugs.
    if (isOffline()) {
      return null
    }
    event.request = {
      ...event.request,
      headers: {},
    }
    return event
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
      delete breadcrumb.data?.['request_body']
    }
    return breadcrumb
  },
  siblingOptions: {
    vueOptions: { app, attachProps: false, attachErrorHandler: true },
  },
})

// A deep link is attacker-supplied text as far as the router is concerned, so a
// path it cannot parse is treated as no deep link at all rather than thrown out
// of init() before the app is mounted.
const resolveStartupDeepLink = (path: string | null): RouteLocationResolved | null => {
  if (!path) return null
  try {
    return router.resolve(path)
  } catch (error) {
    console.error('Could not resolve startup deep link', path, error)
    return null
  }
}

const accessibleWithoutSession = [
  '/auth',
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/callback/google',
  '/auth/callback/apple',
  '/auth/mobile-callback/google',
  '/auth/mobile-callback/apple',
]

type CapgoChannel = 'dev' | 'supporters' | 'production'

const resolveCapgoChannel = (isTester: boolean, hasPremium: boolean): CapgoChannel => {
  if (isTester) return 'dev'
  if (hasPremium) return 'supporters'
  return 'production'
}

const init = async () => {
  await restoreAuthState()
  patchGlobalFetch()
  initSuperTokens()

  const { init: authStoreInit, signOut } = useAuthStore()
  // Preference store is needed in other stores so we initialize it first
  await usePreferencesStore().init()
  // Info store before auth so isOffline (connectivity + force-offline) is settled:
  // auth.init reads it to skip the network session refresh offline (else the user
  // gets logged out). localization/theme come AFTER catalog-sync so their catalog
  // reads (volume suffixes, publishers) route through the replica offline.
  await useInfoStore().init()
  const hasSession = await authStoreInit()

  // Init the catalog-sync store now: creating it registers the offline read
  // handlers + replica gate (synchronously) BEFORE any catalog read below —
  // otherwise an early feed/lookup query races ahead of registration and falls
  // through to the network, hitting the wire offline. Auth is settled first so the
  // gate's entitlement check is valid. init() kicks the non-blocking fill.
  useCatalogSyncStore().init()

  // Volume suffixes (a replicated lookup) load through catalogQuery, so they must
  // run after registration above.
  await useLocalizationStore().init()
  // Restore the offline-durable settings queue + snapshot (pulls fresh server settings
  // when authed + online), then reflect the server's language on this device. The
  // settings overlay makes a pending offline language change win, so this is a no-op
  // when there is one and a server pull otherwise.
  await useUserSettingsStore().init()
  if (hasSession) {
    useLocalizationStore().reconcileLanguageFromServer()
    // Needs the settings store: the resulting FCM token is queued as a settings edit.
    void enablePushNotificationsOnFirstStart()
  }
  // Theme store fetches publishers (a replicated lookup) via catalogQuery.
  await useThemeStore().init()

  if (Capacitor.isNativePlatform()) {
    const { isTester, hasPremium } = storeToRefs(useAuthStore())
    // isTester flaps during auth resolution (undefined → false → true). setChannel
    // hits the Capgo API, which rate-limits hard (429 → "Stopping all stats and
    // channel requests until app restart"), after which a tester never lands on the
    // dev channel and checkForUpdate reports "no update". Only call it when the
    // resolved target channel actually changes.
    let lastChannel: CapgoChannel | null = null
    watch(
      () => resolveCapgoChannel(isTester.value, hasPremium.value),
      async (channel) => {
        if (channel === lastChannel) return
        lastChannel = channel
        try {
          await CapacitorUpdater.setChannel({ channel })
        } catch (err) {
          // Reset so a transient failure (e.g. 429) is retried on the next change.
          lastChannel = null
          console.warn('Failed to set Capgo channel', err)
        }
      },
      { immediate: true },
    )
  }

  const { isWeb } = useInfoStore()
  await usePaymentStore().init()

  // User collections should only be loaded after auth store is set up
  await useUserCollectionStore().init()

  // Resume any list-membership edits queued offline / before a previous kill.
  await useUserListsStore().init()

  // Lists store fetches admin-defined database lists and the user's custom lists.
  // Use allSettled so a CMS hiccup doesn't block app boot.
  const listsStore = useListsStore()
  await Promise.allSettled([listsStore.loadDatabaseLists(), listsStore.loadCustomLists()])

  const { startPage } = usePreferencesStore()
  // Where the app should end up is decided BEFORE mounting, but navigated to
  // AFTER it. Resolving is synchronous and pulls no route chunk, so the auth gate
  // below still sees the deep link's own path; the navigation itself does pull a
  // lazy chunk, and a chunk that rejects or never settles must not be able to
  // strand the user on an unmounted, blank shell. Anything arriving later (a
  // notification tap, an appUrlOpen link) navigates on top of the result.
  const deepLinkTarget = resolveStartupDeepLink(await takeStartupDeepLinkPath())
  if (deepLinkTarget) {
    useInfoStore().claimStartupNavigation()
  }

  let startupTarget: RouteLocationRaw | null = deepLinkTarget?.fullPath ?? null
  const targetPath = deepLinkTarget?.path ?? location.pathname
  if (!hasSession && !accessibleWithoutSession.includes(targetPath)) {
    await signOut()
    startupTarget = { name: RouteName.AuthIndex }
  }
  const { hasPremium, isTester } = useAuthStore()
  if (isWeb && hasSession && !hasPremium && !isTester) {
    startupTarget = { name: RouteName.Settings }
  }
  if (shouldNavigateToStartPage(isWeb, hasSession, useInfoStore().startupNavigationClaimed)) {
    startupTarget = { name: START_PAGE_ROUTE_NAME[startPage] }
  }

  app.mount('#app')

  if (startupTarget) {
    await router.replace(startupTarget).catch((error: unknown) => {
      console.error('Startup navigation failed', error)
    })
  }
}
router.isReady().then(() => {
  init().catch(async (e) => {
    if (e instanceof Error) {
      console.error(e)
      await Dialog.alert({
        title: 'error',
        message: e?.stack || 'unknown error',
      })
    }
  })
})
